import React, { useState, useEffect  } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ProductSlider from './ProductSlider';


export default function DigitalProducts({ onBuyNow, setProductSliderReset, onOffersDetected }) {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [recentSearches, setRecentSearches] = useState([]);
  // eslint-disable-next-line
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line
  const [error, setError] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState(["All"]); // New state for dynamic categories
  const [quantitiesByProductId, setQuantitiesByProductId] = useState({});
  
    useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Changed the fetch URL to get all products from the single JSON file
        const response = await fetch("https://a3creativehub.in/api/products");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProducts(data);

        // Extract unique categories from fetched products
        const uniqueCategories = [...new Set(data.map(p => p.productCategory))];
        setDynamicCategories(["All", ...uniqueCategories]); // Add "All" and set dynamic categories

        const offersExist = data.some(p => p.productOffer && p.productOffer.trim() !== '');
        if (onOffersDetected) {
          onOffersDetected(offersExist);
        }

      } catch (e) {
        setError("Failed to fetch products. Please ensure the backend is running and 'Products.json' exists.");
        console.error("Error fetching products:", e);
        if (onOffersDetected) { // Ensure to call onOffersDetected even on error
          onOffersDetected(false);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [onOffersDetected]); // Empty dependency array means this runs once on mount
  
  const handleSearch = (value) => {
    setSearchQuery(value);
    if (value.trim() && !recentSearches.includes(value)) {
      setRecentSearches((prev) => [value, ...prev.slice(0, 4)]);
    }
  };

  const filteredProducts = products
    .filter((p) =>
      selectedCategory === "All" ? true : p.productCategory === selectedCategory
    )
    .filter((p) => {
      const q = searchQuery.toLowerCase();
      return (
        p.productName.toLowerCase().includes(q) ||
        p.productDescription.toLowerCase().includes(q) ||
        p.productName.toLowerCase().split(" ").some((word) => word.startsWith(q)) ||
        p.productDescription.toLowerCase().split(" ").some((word) => word.startsWith(q))
      );
    });

  return (
    <section
      id="products"
      className="relative overflow-hidden pt-16 bg-gradient-to-r from-pink-200 via-yellow-100 to-pink-100 py-20 px-4 scroll-mt-16"
    > 
      <motion.h2
        className="text-4xl font-bold text-pink-600 mb-10 text-center"
        initial={{ opacity: 0, y: -50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.05 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        Products
      </motion.h2>

      <motion.h4>
      {/* Marquee will be placed here */}
      <div className="relative w-full overflow-hidden whitespace-nowrap mb-8 py-2 bg-pink-50 text-pink-700 text-lg font-medium">
        <div className="inline-block animate-marquee pl-[100%]">
          <span className="px-8">Choose from the wide variety of Products!</span>
          <span className="px-8">Select the quantity of your desired product and click Buy now below the quantity.</span>
          <span className="px-8">If you are interested about multiple products, then select quantities of the products you like and click "Buy Now(Selected)".</span>
          <span className="px-8">You will re-directed to WhatsApp chat to know more about the product from one of our representatives.</span>
          {/* Duplicate content for seamless loop */}
          <span className="px-8">Choose from the wide variety of Products!</span>
          <span className="px-8">Select the quantity of your desired product and click Buy now below the quantity.</span>
          <span className="px-8">If you are interested about multiple products, then select quantities of the products you like and click "Buy Now(Selected)".</span>
          <span className="px-8">You will re-directed to WhatsApp chat to know more about the product from one of our representatives.</span>
        </div>
      </div>
      </motion.h4>
      {/* End Marquee */}

      <div className="flex justify-center mb-8">
        <label
          htmlFor="category-select"
          className="mb-2 px-2 text-lg font-semibold text-pink-600 tracking-wide"
        >
          Select Category
        </label>
        <select
          id="category-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 rounded-full border border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-500 w-64 text-sm bg-gradient-to-r from-yellow-100 to-pink-100"
        >
          {dynamicCategories.map((productCategory) => (
            <option key={productCategory} value={productCategory}>
              {productCategory}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-center mb-4">
        <input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="px-4 py-2 rounded-full border border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-500 w-64 text-sm"
        />
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center text-pink-600 font-semibold text-lg py-10">
          {"🚫 No products found matching your search."} {/* Emojis inside strings in JSX need to be in curly braces */}
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory + searchQuery}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <ProductSlider products={filteredProducts} allProducts={products} onBuyNow={onBuyNow} setResetFunction={setProductSliderReset} quantities={quantitiesByProductId} setQuantities={setQuantitiesByProductId}/>  {/*Pass the setter down*/}
          </motion.div>
        </AnimatePresence>
      )}
    </section>
  );
}